// Dashboard Operacional (ADR-0032, issue #89): números de destaque com
// minigráfico, tendência por período (volume e taxas) e, na visão Rankings ou
// na visão Quadro Comparativo, onde estão a divergência e a relavagem — por
// Cliente, Categoria, Tipo de Peça ou Motivo. Clicar num item filtra a tela.
// As contas são do domínio (`indicadores-dashboard.ts`); aqui só se desenha.

import {
  indicadoresDashboard,
  indicadoresVazios,
  periodoAnterior,
  taxaDivergencia,
  taxaReclamacao,
  taxaRelavagem,
  totalLavado,
  type Agrupamento,
  type FiltroIndicadores,
  type Granularidade,
  type Indicadores,
} from "../../domain/indicadores-dashboard.ts";
import { hojeISO, type DataISO } from "../../domain/datas.ts";
import type { Snapshot } from "../../domain/snapshot.ts";
import type { Store } from "../store.ts";
import { clear, dicaInfo, el, lerJSONValidado, salvarJSON } from "../ui.ts";
import { ajustesExibicao, comAjustes } from "./_ajustes.ts";
import { barraFiltros, filtroData } from "./_cadastro.ts";
import { multiSelect, type MultiSelect } from "./_multi-select.ts";
import {
  formatarNumero,
  formatarTaxa,
  graficoBarras,
  graficoLinhas,
  legendaGrafico,
  miniGrafico,
  rankingBarras,
  type ItemRanking,
  type ParteSerie,
  type RotulosGrafico,
  type SerieBarras,
  type SerieLinha,
} from "./_grafico.ts";

const COR = {
  volume: "var(--serie-volume)",
  devolvidas: "var(--serie-devolvidas)",
  divergencia: "var(--serie-divergencia)",
  relavagem: "var(--serie-relavagem)",
  reclamacao: "var(--serie-reclamacao)",
  backlog: "var(--serie-backlog)",
} as const;
const CORES_MOTIVO = 5;
const SEM_MOTIVO = "(sem motivo)";

type Visao = "rankings" | "quadro";
type Medir = "recebido" | "lavado";
type Bloco = "divergencia" | "relavagem" | "volume";
type PorItem = "cliente" | "categoria" | "tipo";
type Agrupar = PorItem | "motivo";
type ColunaQuadro = "volume" | "divergencia" | "relavagem" | "reclamacao";

/** Preferências de apresentação, lembradas no navegador (não os filtros de recorte). */
interface Preferencias {
  granularidade: Granularidade;
  visao: Visao;
  medir: Medir;
  agrupar: Record<Bloco, Agrupar>;
  agruparQuadro: PorItem;
  ordemQuadro: ColunaQuadro;
  /** Ajustes de exibição: o valor de cada período (não só o atual) nos gráficos de tendência. */
  valoresTodosPeriodos: boolean;
}
const CHAVE_PREFERENCIAS = "dashboard:preferencias";
const PADRAO: Preferencias = {
  granularidade: "mes",
  visao: "rankings",
  medir: "recebido",
  agrupar: { divergencia: "cliente", relavagem: "tipo", volume: "cliente" },
  agruparQuadro: "cliente",
  ordemQuadro: "divergencia",
  valoresTodosPeriodos: false,
};
const umDe = <T extends string>(v: unknown, opcoes: readonly T[]): v is T => typeof v === "string" && (opcoes as readonly string[]).includes(v);
/** O que pode estar guardado: preferência salva antes de uma opção existir não a tem (vale o padrão). */
type PreferenciasSalvas = Omit<Preferencias, "valoresTodosPeriodos"> & Partial<Pick<Preferencias, "valoresTodosPeriodos">>;
function ehPreferencias(v: unknown): v is PreferenciasSalvas {
  if (typeof v !== "object" || v == null) return false;
  const p = v as Record<string, unknown>;
  const a = p.agrupar as Record<string, unknown> | undefined;
  return (
    umDe(p.granularidade, ["dia", "semana", "mes", "ano"] as const) &&
    umDe(p.visao, ["rankings", "quadro"] as const) &&
    umDe(p.medir, ["recebido", "lavado"] as const) &&
    typeof a === "object" && a != null &&
    umDe(a.divergencia, ["cliente", "categoria", "tipo", "motivo"] as const) &&
    umDe(a.relavagem, ["cliente", "categoria", "tipo"] as const) &&
    umDe(a.volume, ["cliente", "categoria", "tipo"] as const) &&
    umDe(p.agruparQuadro, ["cliente", "categoria", "tipo"] as const) &&
    umDe(p.ordemQuadro, ["volume", "divergencia", "relavagem", "reclamacao"] as const) &&
    (p.valoresTodosPeriodos === undefined || typeof p.valoresTodosPeriodos === "boolean")
  );
}

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
/** Rótulo curto do eixo X: "04/10", "S40/26", "set/26", "2026". */
function rotuloPeriodo(periodo: string, g: Granularidade): string {
  switch (g) {
    case "dia":
      return `${periodo.slice(8, 10)}/${periodo.slice(5, 7)}`;
    case "semana": {
      const m = /^(\d{4})-W(\d{2})$/.exec(periodo);
      return m ? `S${m[2]}/${m[1]!.slice(2)}` : periodo;
    }
    case "mes":
      return `${MESES[Number(periodo.slice(5, 7)) - 1]}/${periodo.slice(2, 4)}`;
    case "ano":
      return periodo;
  }
}

/** Início padrão: o 1º dia de N meses antes do mês corrente (ADR-0032). */
function inicioPadrao(hoje: DataISO, meses: number): DataISO {
  const [a, m] = hoje.split("-").map(Number) as [number, number];
  const d = new Date(Date.UTC(a, m - 1 - meses, 1));
  return d.toISOString().slice(0, 10) as DataISO;
}

/** Desenha na largura real do contêiner, depois do layout (evita reflow síncrono). */
function desenharDepois(box: HTMLElement, desenhar: (largura: number) => Node): HTMLElement {
  requestAnimationFrame(() => box.append(desenhar(box.clientWidth)));
  return box;
}

function segmentado<T extends string>(rotulo: string, opcoes: readonly [T, string][], atual: T, aoEscolher: (v: T) => void): HTMLElement {
  const grupo = el("div", { class: "segmentado", role: "group", "aria-label": rotulo });
  for (const [v, r] of opcoes) {
    const b = el("button", { type: "button", "aria-pressed": String(v === atual) }, r);
    b.addEventListener("click", () => aoEscolher(v));
    grupo.append(b);
  }
  return grupo;
}

function seletor<T extends string>(rotulo: string, opcoes: readonly [T, string][], atual: T, aoEscolher: (v: T) => void): HTMLElement {
  const sel = el("select", {}) as HTMLSelectElement;
  for (const [v, r] of opcoes) {
    const o = el("option", { value: v }, r) as HTMLOptionElement;
    o.selected = v === atual;
    sel.append(o);
  }
  sel.addEventListener("change", () => aoEscolher(sel.value as T));
  return el("label", { class: "filtro" }, rotulo, sel);
}

const POR_ITEM: readonly [PorItem, string][] = [["cliente", "Cliente"], ["categoria", "Categoria"], ["tipo", "Tipo de Peça"]];

export function renderDashboard(container: HTMLElement, store: Store): () => void {
  const hoje = hojeISO();
  let dataInicio: DataISO | null = null; // null = padrão dos Parâmetros
  let dataFim: DataISO | null = null;
  const pref: Preferencias = { ...structuredClone(PADRAO), ...lerJSONValidado(CHAVE_PREFERENCIAS, ehPreferencias) };
  const guardar = (): void => salvarJSON(CHAVE_PREFERENCIAS, pref);
  const ajustes = ajustesExibicao([
    {
      rotulo: "Valores de todos os períodos nos gráficos de tendência",
      ligada: () => pref.valoresTodosPeriodos,
      aoMudar: (v) => { pref.valoresTodosPeriodos = v; guardar(); render(); },
    },
  ]);
  const verTodos: Record<Bloco, boolean> = { divergencia: false, relavagem: false, volume: false };
  let marcados: Record<PorItem, string[] | null> = { cliente: null, categoria: null, tipo: null };

  const ms: Record<PorItem, MultiSelect> = {
    cliente: multiSelect({ rotulo: "Cliente", itens: [], aoMudar: () => { marcados.cliente = ms.cliente.selecionados(); render(); } }),
    categoria: multiSelect({
      rotulo: "Categoria",
      itens: [],
      // muda a Categoria: o Tipo de Peça volta a "todos" da cascata nova
      aoMudar: () => { marcados.categoria = ms.categoria.selecionados(); marcados.tipo = null; render(); },
    }),
    tipo: multiSelect({ rotulo: "Tipo de Peça", itens: [], aoMudar: () => { marcados.tipo = ms.tipo.selecionados(); render(); } }),
  };

  /** Clicar num item = só ele no filtro; clicar de novo = todos (ADR-0032, Q25). */
  const escolhido = (por: PorItem) => (chave: string): boolean => {
    const sel = marcados[por];
    return sel != null && sel.length === 1 && sel[0] === chave;
  };
  const filtrarPor = (por: PorItem) => (chave: string): void => {
    marcados[por] = escolhido(por)(chave) ? null : [chave];
    if (por === "categoria") marcados.tipo = null;
    render();
  };

  const render = (): void => {
    clear(container);
    const snap = store.get();
    const inicio = dataInicio ?? inicioPadrao(hoje, snap.config.mesesPadraoDashboard);
    const fim = dataFim ?? hoje;
    const g = pref.granularidade;

    // Sem filtrar por `.ativo`: é relatório do que já aconteceu.
    const itens = (xs: readonly { id: string; nome: string; ativo: boolean }[]) =>
      [...xs].map((x) => ({ id: x.id, nome: x.ativo ? x.nome : `${x.nome} (inativo)` })).sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
    ms.cliente.atualizarItens(itens(snap.clientes));
    ms.categoria.atualizarItens(itens(snap.categorias));
    if (marcados.cliente) ms.cliente.definirSelecionados(marcados.cliente); else ms.cliente.marcarTodos();
    if (marcados.categoria) ms.categoria.definirSelecionados(marcados.categoria); else ms.categoria.marcarTodos();
    const categoriasMarcadas = new Set(ms.categoria.selecionados());
    ms.tipo.atualizarItens(itens(snap.tiposPeca.filter((t) => categoriasMarcadas.has(t.categoriaId))));
    if (marcados.tipo) ms.tipo.definirSelecionados(marcados.tipo); else ms.tipo.marcarTodos();

    const filtros = comAjustes(barraFiltros(
      [
        ms.cliente.element, ms.categoria.element, ms.tipo.element,
        filtroData("De", inicio, (v) => { if (v) { dataInicio = v as DataISO; render(); } }),
        filtroData("Até", fim, (v) => { if (v) { dataFim = v as DataISO; render(); } }),
        seletor("Granularidade", [["dia", "Por Dia"], ["semana", "Por Semana"], ["mes", "Por Mês"], ["ano", "Por Ano"]], g, (v) => {
          pref.granularidade = v; guardar(); render();
        }),
      ],
      () => {
        marcados = { cliente: null, categoria: null, tipo: null };
        dataInicio = dataFim = null;
        render();
      },
    ), ajustes);

    const recorte: FiltroIndicadores = {
      dataInicio: inicio,
      dataFim: fim,
      clienteIds: ms.cliente.selecionados(),
      categoriaIds: ms.categoria.selecionados(),
      tipoIds: ms.tipo.selecionados(),
    };
    const calcular = (a: Agrupamento, f: FiltroIndicadores = recorte) => indicadoresDashboard(snap, f, a);
    const tot = calcular({ por: "total" }).get("")!;
    const ant = calcular({ por: "total" }, { ...recorte, ...periodoAnterior(inicio, fim) }).get("")!;
    const porPeriodo = calcular({ por: "periodo", granularidade: g });
    const periodos = [...porPeriodo.keys()];
    const rotulos = periodos.map((p) => rotuloPeriodo(p, g));
    const serie = <T>(fn: (i: Indicadores) => T): T[] => periodos.map((p) => fn(porPeriodo.get(p) ?? indicadoresVazios()));

    const raiz = el("div", { class: "dashboard" });
    container.append(filtros, raiz);
    if (tot.osAbertas > 0) {
      raiz.append(el("div", { class: "aviso aviso-atencao", role: "status" },
        `${formatarNumero(tot.osAbertas)} ${tot.osAbertas === 1 ? "OS deste período ainda não foi fechada" : "OS deste período ainda não foram fechadas"} — ficam fora das taxas, que podem mudar quando forem fechadas.`));
    }

    raiz.append(numerosDeDestaque(tot, ant, serie, rotulos));
    raiz.append(tendencia(serie, rotulos, pref, () => { guardar(); render(); }));
    raiz.append(el("div", { class: "dashboard-visao" }, el("span", {}, "Visão"),
      segmentado<Visao>("Visão", [["rankings", "Rankings"], ["quadro", "Quadro Comparativo"]], pref.visao, (v) => {
        pref.visao = v; guardar(); render();
      })));

    const nomes = new Map<string, string>([...snap.clientes, ...snap.categorias, ...snap.tiposPeca].map((x) => [x.id, x.nome]));
    const motivos = [...snap.listas.motivosDivergencia].sort((a, b) => a.localeCompare(b, "pt-BR"));
    const corMotivo = (m: string): string => {
      const i = motivos.indexOf(m);
      return i >= 0 && i < CORES_MOTIVO ? `var(--serie-motivo-${i + 1})` : "var(--texto-desabilitado)";
    };
    /** Motivos de um grupo, na ordem alfabética da lista (a ordem validada). */
    const partesMotivo = (i: Indicadores): ItemRanking["partes"] => {
      const ordem = (m: string): number => (motivos.includes(m) ? motivos.indexOf(m) : motivos.length);
      return Object.entries(i.divergencia.porMotivo)
        .sort(([a], [b]) => ordem(a) - ordem(b) || a.localeCompare(b, "pt-BR"))
        .map(([m, n]) => ({ rotulo: m || SEM_MOTIVO, cor: corMotivo(m), valor: n }));
    };
    const minimo = snap.config.volumeMinimoRanking;
    const ctx: ContextoBlocos = { snap, calcular, tot, nomes, corMotivo, partesMotivo, minimo, pref, verTodos, escolhido, filtrarPor, motivos, render: () => { guardar(); render(); } };

    if (pref.visao === "rankings") {
      raiz.append(el("div", { class: "dashboard-grade" }, blocoRanking("divergencia", ctx), blocoRanking("relavagem", ctx), blocoRanking("volume", ctx)));
    } else {
      raiz.append(quadroComparativo(ctx));
    }
  };

  render();
  return store.subscribe(render);
}

// --- números de destaque -------------------------------------------------------

function numerosDeDestaque(
  tot: Indicadores,
  ant: Indicadores,
  serie: <T>(fn: (i: Indicadores) => T) => T[],
  rotulos: readonly string[],
): HTMLElement {
  const comparacao = (atual: number | null, antes: number | null, ehTaxa: boolean): string => {
    if (atual == null || antes == null || (!ehTaxa && antes === 0)) return "sem base no período anterior";
    const d = ehTaxa ? (atual - antes) * 100 : ((atual - antes) / antes) * 100;
    const seta = Math.abs(d) < 0.05 ? "=" : d > 0 ? "↑" : "↓";
    const v = Math.abs(d).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    return `${seta} ${v}${ehTaxa ? " p.p." : "%"} vs. período anterior`;
  };
  const card = (rotulo: string, cor: string, valor: string, apoio: string, comp: string, valores: (number | null)[], ehTaxa: boolean, dica: HTMLElement[]): HTMLElement => {
    const mini = el("div", { class: "dashboard-destaque-mini" });
    desenharDepois(mini, (largura) => miniGrafico({
      valores, cor, largura,
      rotuloDe: (i, v) => `${rotulos[i]}: ${ehTaxa ? formatarTaxa(v) : formatarNumero(v)}`,
    }));
    return el("div", { class: "dashboard-destaque" },
      el("span", { class: "dashboard-destaque-rotulo" }, el("span", { class: "grafico-legenda-cor", style: `background:${cor}` }), rotulo,
        dicaInfo(`Como é calculado: ${rotulo}`, ...dica)),
      el("strong", { class: "dashboard-destaque-valor" }, valor),
      el("span", { class: "dashboard-destaque-apoio" }, apoio),
      el("span", { class: "dashboard-destaque-comparacao" }, comp),
      mini);
  };
  return el("div", { class: "dashboard-destaques" },
    card("Volume Recebido", COR.volume, formatarNumero(tot.recebido), "peças recebidas (Comanda)",
      comparacao(tot.recebido, ant.recebido, false), serie((i) => i.recebido), false, EXPLICA.recebido()),
    card("% Divergência", COR.divergencia, formatarTaxa(taxaDivergencia(tot)),
      `${formatarNumero(tot.divergencia.pecas)} de ${formatarNumero(tot.divergencia.base)} recebidas`,
      comparacao(taxaDivergencia(tot), taxaDivergencia(ant), true), serie(taxaDivergencia), true, EXPLICA.divergencia()),
    card("% Relavagem Interna", COR.relavagem, formatarTaxa(taxaRelavagem(tot)),
      `${formatarNumero(tot.relavagem.interna)} de ${formatarNumero(tot.relavagem.base)} conferidas`,
      comparacao(taxaRelavagem(tot), taxaRelavagem(ant), true), serie(taxaRelavagem), true, [EXPLICA.relavagem()[0]!]),
    card("% Reclamação", COR.reclamacao, formatarTaxa(taxaReclamacao(tot)),
      `${formatarNumero(tot.reclamacao)} peças em OS de Relavagem`,
      comparacao(taxaReclamacao(tot), taxaReclamacao(ant), true), serie(taxaReclamacao), true,
      [p(forte("Reclamação"), ": peças das OS de Relavagem (o hotel reclamou depois da entrega), pela data da coleta delas, ÷ a Conferida das OS devolvidas no período.")]));
}

// --- tendência -----------------------------------------------------------------

function seletorMedir(pref: Preferencias, mudou: () => void): HTMLElement {
  return segmentado<Medir>("Medir", [["recebido", "Recebido"], ["lavado", "Lavado"]], pref.medir, (v) => { pref.medir = v; mudou(); });
}

function tendencia(
  serie: <T>(fn: (i: Indicadores) => T) => T[],
  periodos: readonly string[],
  pref: Preferencias,
  mudou: () => void,
): HTMLElement {
  const volume: SerieBarras = pref.medir === "recebido"
    ? { rotulo: "Recebido", partes: [{ rotulo: "Recebido", cor: COR.volume, valores: serie((i) => i.recebido) }] }
    : {
        rotulo: "Lavado",
        partes: [
          { rotulo: "Comanda", cor: COR.volume, valores: serie((i) => i.lavado.comanda) },
          { rotulo: "Reclamação", cor: COR.reclamacao, valores: serie((i) => i.lavado.reclamacao) },
          { rotulo: "Backlog Reprocessado", cor: COR.backlog, valores: serie((i) => i.lavado.backlog) },
        ],
      };
  const devolvidas: SerieBarras = { rotulo: "Devolvidas", partes: [{ rotulo: "Devolvidas", cor: COR.devolvidas, valores: serie((i) => i.devolvidas) }] };
  const barras = [volume, devolvidas];
  const taxas: SerieLinha[] = [
    { rotulo: "% Divergência", cor: COR.divergencia, valores: serie(taxaDivergencia) },
    { rotulo: "% Relavagem Interna", cor: COR.relavagem, valores: serie(taxaRelavagem) },
    { rotulo: "% Reclamação", cor: COR.reclamacao, valores: serie(taxaReclamacao) },
  ];
  const caixa = (): HTMLElement => el("div", { class: "dashboard-grafico" });
  const rotulos: RotulosGrafico = pref.valoresTodosPeriodos ? "todos" : "atual";
  const rolarParaOFim = (n: HTMLElement): HTMLElement => {
    // com rolagem, abre já no período mais recente
    requestAnimationFrame(() => { n.scrollLeft = n.scrollWidth; });
    return n;
  };
  return el("div", { class: "dashboard-grade" },
    el("section", { class: "card" },
      el("div", { class: "dashboard-cartao-topo" }, el("h2", {}, "Volume por Período", dicaInfo("Como é calculado: Volume por Período", ...EXPLICA.recebido(), ...EXPLICA.lavado(),
        p(forte("Devolvidas"), ": o total entregue (da própria OS + o que voltou do Backlog), pela data da devolução."))), el("div", { class: "dashboard-controles" }, seletorMedir(pref, mudou))),
      legendaGrafico(barras.flatMap((s): ParteSerie[] => [...s.partes])),
      desenharDepois(caixa(), (largura) => rolarParaOFim(graficoBarras({ periodos, series: barras, larguraDisponivel: largura, rotulos })))),
    el("section", { class: "card" },
      el("div", { class: "dashboard-cartao-topo" }, el("h2", {}, "Taxas por Período", dicaInfo("Como é calculado: Taxas por Período", ...EXPLICA.divergencia(), ...EXPLICA.relavagem()))),
      legendaGrafico(taxas),
      desenharDepois(caixa(), (largura) => rolarParaOFim(graficoLinhas({ periodos, series: taxas, larguraDisponivel: largura, rotulos })))));
}

// --- rankings e quadro ---------------------------------------------------------

interface ContextoBlocos {
  snap: Snapshot;
  calcular: (a: Agrupamento) => Map<string, Indicadores>;
  tot: Indicadores;
  nomes: ReadonlyMap<string, string>;
  corMotivo: (m: string) => string;
  partesMotivo: (i: Indicadores) => ItemRanking["partes"];
  motivos: readonly string[];
  minimo: number;
  pref: Preferencias;
  verTodos: Record<Bloco, boolean>;
  escolhido: (por: PorItem) => (chave: string) => boolean;
  filtrarPor: (por: PorItem) => (chave: string) => void;
  render: () => void;
}

const TITULO: Record<Bloco, string> = {
  divergencia: "Divergência",
  relavagem: "Relavagem",
  volume: "Volume",
};
/** Como cada número é calculado (ADR-0032) — o conteúdo das dicas "i". */
const p = (...partes: (string | HTMLElement)[]): HTMLElement => el("p", {}, ...partes);
const forte = (t: string): HTMLElement => el("strong", {}, t);
const EXPLICA = {
  recebido: () => [p(forte("Recebido"), ": o que o hotel declarou na Comanda, pela data da coleta. OS de Relavagem não entram.")],
  lavado: () => [p(forte("Lavado"), ": o que a lavanderia lavou, pela data da coleta — Comanda (a Conferida) + Reclamação + peças do Backlog reprocessadas na OS.")],
  divergencia: () => [
    p("Peças contadas erradas — a mais ou a menos, somadas — ÷ ", forte("Recebida"), " (o que o hotel declarou)."),
    p("Só OS fechadas, pela data da ", forte("coleta"), ". A barra mostra o motivo."),
  ],
  relavagem: () => [
    p(forte("Relavagem Interna"), ": peças retidas com o motivo Relavagem ÷ ", forte("Conferida"), ". Só OS fechadas, pela data da ", forte("devolução"), "."),
    p(forte("Reclamação"), ": peças das OS de Relavagem, pela data da coleta delas, sobre a mesma base."),
  ],
  minimo: (n: number) => p(`Item com menos de ${formatarNumero(n)} peças na base vai para o fim, esmaecido.`),
};

function itensDoBloco(bloco: Bloco, ctx: ContextoBlocos): ItemRanking[] {
  const agr = ctx.pref.agrupar[bloco];
  const { tot } = ctx;
  if (agr === "motivo") {
    return ctx.partesMotivo(tot).map((p) => ({
      chave: p.rotulo, rotulo: p.rotulo, valor: p.valor, partes: [p], volumeBaixo: false,
      texto: `${formatarTaxa(tot.divergencia.base > 0 ? p.valor / tot.divergencia.base : null, 2)} · ${formatarNumero(p.valor)} peças`,
    }));
  }
  const itens: ItemRanking[] = [];
  const totalVolume = ctx.pref.medir === "recebido" ? tot.recebido : totalLavado(tot);
  for (const [chave, i] of ctx.calcular({ por: agr })) {
    const rotulo = ctx.nomes.get(chave) ?? chave;
    if (bloco === "divergencia") {
      const t = taxaDivergencia(i);
      if (t == null) continue;
      itens.push({ chave, rotulo, valor: t, partes: ctx.partesMotivo(i), volumeBaixo: i.divergencia.base < ctx.minimo,
        texto: `${formatarTaxa(t)} · ${formatarNumero(i.divergencia.pecas)} de ${formatarNumero(i.divergencia.base)}` });
    } else if (bloco === "relavagem") {
      if (i.relavagem.base === 0) continue;
      const pecas = i.relavagem.interna + i.reclamacao;
      itens.push({ chave, rotulo, valor: pecas / i.relavagem.base, volumeBaixo: i.relavagem.base < ctx.minimo,
        partes: [
          { rotulo: "Relavagem Interna", cor: COR.relavagem, valor: i.relavagem.interna },
          { rotulo: "Reclamação", cor: COR.reclamacao, valor: i.reclamacao },
        ],
        texto: `${formatarTaxa(pecas / i.relavagem.base)} · ${formatarNumero(pecas)} de ${formatarNumero(i.relavagem.base)}` });
    } else {
      const v = ctx.pref.medir === "recebido" ? i.recebido : totalLavado(i);
      if (v === 0) continue;
      itens.push({ chave, rotulo, valor: v, volumeBaixo: false,
        partes: ctx.pref.medir === "recebido"
          ? [{ rotulo: "Recebido", cor: COR.volume, valor: v }]
          : [
              { rotulo: "Comanda", cor: COR.volume, valor: i.lavado.comanda },
              { rotulo: "Reclamação", cor: COR.reclamacao, valor: i.lavado.reclamacao },
              { rotulo: "Backlog Reprocessado", cor: COR.backlog, valor: i.lavado.backlog },
            ],
        texto: `${formatarNumero(v)} · ${formatarTaxa(totalVolume > 0 ? v / totalVolume : null, 0)} do total` });
    }
  }
  return itens;
}

function legendaDoBloco(bloco: Bloco, ctx: ContextoBlocos): HTMLElement {
  if (bloco === "divergencia") {
    return ctx.pref.agrupar.divergencia === "motivo" ? el("span") : legendaGrafico(ctx.partesMotivo(ctx.tot));
  }
  if (bloco === "relavagem") {
    return legendaGrafico([{ rotulo: "Relavagem Interna", cor: COR.relavagem }, { rotulo: "Reclamação", cor: COR.reclamacao }]);
  }
  return ctx.pref.medir === "lavado"
    ? legendaGrafico([{ rotulo: "Comanda", cor: COR.volume }, { rotulo: "Reclamação", cor: COR.reclamacao }, { rotulo: "Backlog Reprocessado", cor: COR.backlog }])
    : el("span");
}

function blocoRanking(bloco: Bloco, ctx: ContextoBlocos): HTMLElement {
  const agr = ctx.pref.agrupar[bloco];
  const opcoes: [Agrupar, string][] = bloco === "divergencia" ? [...POR_ITEM, ["motivo", "Motivo"]] : [...POR_ITEM];
  const controles = el("div", { class: "dashboard-controles" },
    bloco === "volume" ? seletorMedir(ctx.pref, ctx.render) : "",
    seletor<Agrupar>("Agrupar por", opcoes, agr, (v) => { ctx.pref.agrupar[bloco] = v; ctx.verTodos[bloco] = false; ctx.render(); }));
  return el("section", { class: "card" },
    el("div", { class: "dashboard-cartao-topo" },
      el("h2", {}, TITULO[bloco], dicaInfo(`Como é calculado: ${TITULO[bloco]}`,
        ...(bloco === "volume" ? [...EXPLICA.recebido(), ...EXPLICA.lavado()] : [...EXPLICA[bloco](), EXPLICA.minimo(ctx.minimo)]))),
      controles),
    legendaDoBloco(bloco, ctx),
    rankingBarras({
      itens: itensDoBloco(bloco, ctx),
      verTodos: ctx.verTodos[bloco],
      aoVerTodos: () => { ctx.verTodos[bloco] = !ctx.verTodos[bloco]; ctx.render(); },
      ...(agr !== "motivo" ? { aoEscolher: ctx.filtrarPor(agr), escolhido: ctx.escolhido(agr) } : {}),
      vazio: bloco === "divergencia" && agr === "motivo" ? "Nenhuma divergência no período." : "Nada no período.",
    }));
}

function quadroComparativo(ctx: ContextoBlocos): HTMLElement {
  const agr = ctx.pref.agruparQuadro;
  const linhas = [...ctx.calcular({ por: agr })]
    .map(([chave, i]) => ({
      chave, nome: ctx.nomes.get(chave) ?? chave, i,
      volume: i.recebido as number | null,
      divergencia: taxaDivergencia(i),
      relavagem: taxaRelavagem(i),
      reclamacao: taxaReclamacao(i),
    }))
    .filter((l) => l.i.recebido > 0 || l.i.relavagem.base > 0 || l.i.reclamacao > 0);
  const ordem = ctx.pref.ordemQuadro;
  linhas.sort((a, b) => (b[ordem] ?? -1) - (a[ordem] ?? -1) || a.nome.localeCompare(b.nome, "pt-BR"));
  const maximo = (c: ColunaQuadro): number => linhas.reduce((m, l) => Math.max(m, l[c] ?? 0), 0) || 1;
  const mx = { volume: maximo("volume"), divergencia: maximo("divergencia"), relavagem: maximo("relavagem"), reclamacao: maximo("reclamacao") };

  const celula = (v: number | null, max: number, partes: ItemRanking["partes"], texto: string, baixo: boolean): HTMLElement => {
    const barra = el("span", { class: "ranking-barra", style: `width:${((v ?? 0) / max) * 100}%` });
    const soma = partes.reduce((a, p) => a + p.valor, 0) || 1;
    for (const p of partes) {
      if (p.valor > 0) barra.append(el("span", { style: `flex:${p.valor / soma} 1 0;background:${p.cor}`, title: `${p.rotulo}: ${formatarNumero(p.valor)}` }));
    }
    return el("td", { class: baixo ? "ranking-volume-baixo" : "" },
      el("div", { class: "quadro-celula" }, el("span", { class: "ranking-trilho" }, barra), el("span", { class: "ranking-texto" }, texto)));
  };
  const cabecalho = (rotulo: string, coluna: ColunaQuadro): HTMLElement => {
    // o mesmo cabeçalho ordenável das tabelas (th.ord + button.th-rotulo + seta);
    // aqui só do maior para o menor — quem mais diverge vem primeiro
    const ativo = ordem === coluna;
    const b = el("button", { type: "button", class: "th-rotulo", title: `Ordenar por ${rotulo}` },
      rotulo, ativo ? el("span", { class: "th-seta" }, " ▼") : "");
    b.addEventListener("click", () => { ctx.pref.ordemQuadro = coluna; ctx.render(); });
    return el("th", { scope: "col", class: "ord", "aria-sort": ativo ? "descending" : "none" }, b);
  };
  const escolhido = ctx.escolhido(agr);
  const filtrar = ctx.filtrarPor(agr);
  const corpo = el("tbody", {}, ...linhas.map((l) => {
    const nome = el("button", { type: "button", title: escolhido(l.chave) ? `Tirar o filtro de ${l.nome}` : `Filtrar por ${l.nome}` }, l.nome);
    nome.addEventListener("click", () => filtrar(l.chave));
    const baixoRel = l.i.relavagem.base < ctx.minimo;
    return el("tr", { class: escolhido(l.chave) ? "ranking-escolhido" : "" },
      el("th", { scope: "row" }, nome),
      celula(l.volume, mx.volume, [{ rotulo: "Recebido", cor: COR.volume, valor: l.i.recebido }],
        `${formatarNumero(l.i.recebido)} · ${formatarTaxa(ctx.tot.recebido > 0 ? l.i.recebido / ctx.tot.recebido : null, 0)}`, false),
      celula(l.divergencia, mx.divergencia, ctx.partesMotivo(l.i),
        `${formatarTaxa(l.divergencia)} · ${formatarNumero(l.i.divergencia.pecas)}`, l.i.divergencia.base < ctx.minimo),
      celula(l.relavagem, mx.relavagem, [{ rotulo: "Relavagem Interna", cor: COR.relavagem, valor: l.i.relavagem.interna }],
        `${formatarTaxa(l.relavagem)} · ${formatarNumero(l.i.relavagem.interna)}`, baixoRel),
      celula(l.reclamacao, mx.reclamacao, [{ rotulo: "Reclamação", cor: COR.reclamacao, valor: l.i.reclamacao }],
        `${formatarTaxa(l.reclamacao)} · ${formatarNumero(l.i.reclamacao)}`, baixoRel));
  }));
  const rotuloItem = POR_ITEM.find(([v]) => v === agr)![1];
  const tabela = el("table", { class: "quadro" },
    el("thead", {}, el("tr", {}, el("th", { scope: "col" }, rotuloItem),
      cabecalho("Volume Recebido", "volume"), cabecalho("% Divergência", "divergencia"),
      cabecalho("% Relavagem Interna", "relavagem"), cabecalho("% Reclamação", "reclamacao"))),
    corpo);
  return el("section", { class: "card" },
    el("div", { class: "dashboard-cartao-topo" }, el("h2", {}, "Quadro Comparativo", dicaInfo("Como é calculado: Quadro Comparativo",
      ...EXPLICA.recebido(), ...EXPLICA.divergencia(), ...EXPLICA.relavagem(), EXPLICA.minimo(ctx.minimo))),
      el("div", { class: "dashboard-controles" },
        seletor<PorItem>("Agrupar por", POR_ITEM, agr, (v) => { ctx.pref.agruparQuadro = v; ctx.render(); }))),
    legendaGrafico(ctx.partesMotivo(ctx.tot).map((p) => ({ rotulo: `Divergência: ${p.rotulo}`, cor: p.cor }))),
    linhas.length === 0 ? el("p", { class: "muted" }, "Nada no período.") : el("div", { class: "quadro-wrap" }, tabela));
}
